import { useMemo, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Grid, IconButton } from '@mui/material'
import { GridEventListener } from '@mui/x-data-grid'
import { format, parseJSON } from 'date-fns'
import { useForm } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { ControlledTextField, FplDataGrid, IGridColumn } from 'components/common'
import { DATE_FORMAT, DEBOUNCE_DELAY } from 'constants/index'
import { XeroCodeFilterInput } from 'generated/graphql'
import { GET_PAGED_XERO_CODES } from 'graphql/queries'

import { ServiceCodeDialog } from './ServiceCodeDialog'

const columns: IGridColumn[] = [
  { field: 'code', headerName: 'Code', minWidth: 200, flex: 0.5 },
  { field: 'name', headerName: 'Name', minWidth: 200, flex: 0.5 },
  { field: 'description', headerName: 'Description', minWidth: 200, flex: 1.5 },
  { field: 'type', headerName: 'Type', minWidth: 200, flex: 0.5 },
  {
    field: 'createdAt',
    headerName: 'Created At',
    minWidth: 200,
    flex: 0.5,
    valueFormatter: ({ value }) => value && format(parseJSON(value as string), DATE_FORMAT),
  },
]

const ServiceCodes = () => {
  const [selectedXeroCodeId, setSelectedXeroCodeId] = useState<number | null>(null)
  const [openDialog, setOpenDialog] = useState<boolean>(false)

  const { control, watch, setValue } = useForm({
    shouldUnregister: true,
    defaultValues: {
      search: '',
    },
  })

  const [searchInput] = useDebounce(watch('search', ''), DEBOUNCE_DELAY)
  const searchValue: string = searchInput.length >= 3 ? searchInput : ''

  const handleRowClick: GridEventListener<'rowClick'> = ({ id }) => {
    setSelectedXeroCodeId(Number(id))
    setOpenDialog(true)
  }

  const handleAddXeroCode = () => {
    setSelectedXeroCodeId(null)
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  const handleSearchClearClick = () => {
    setValue('search', '')
  }

  const filter: XeroCodeFilterInput = useMemo(() => {
    return {
      or: [{ name: { contains: searchValue } }],
    }
  }, [searchValue])

  return (
    <>
      <FplDataGrid
        query={GET_PAGED_XERO_CODES}
        entityName='pagedXeroCodes'
        columns={columns}
        filter={filter}
        defaultOrder={{ field: 'createdAt', sort: 'asc' }}
        toolbar={{
          caption: 'Service Codes',
          leftSide: (
            <Grid item>
              <Button variant='outlined' size='small' onClick={handleAddXeroCode}>
                New Service Code
              </Button>
            </Grid>
          ),
          rightSide: (
            <Grid item>
              <Box width='210px'>
                <ControlledTextField
                  control={control}
                  name='search'
                  label='Search'
                  defaultValue=''
                  size='small'
                  endAdornment={
                    searchInput && (
                      <IconButton size='small' onClick={handleSearchClearClick}>
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    )
                  }
                />
              </Box>
            </Grid>
          ),
        }}
        onRowClick={handleRowClick}
      />

      {openDialog && (
        <ServiceCodeDialog
          id={selectedXeroCodeId}
          openDialog={openDialog}
          onCloseDialog={handleCloseDialog}
        />
      )}
    </>
  )
}

export default ServiceCodes
